import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FiPlus } from 'react-icons/fi'
import utility from 'src/utils/utility'
import {
    Box, Flex, Text, tokens,
    Button, SelectField,
    InstitutionTable, TableColumn,
} from 'src/componentv2'
import ConfigurationApiContext, { InstitutionListItem } from 'src/provider/API/v2/call-service/configuration'
import SurveyApiContext, { SurveyTrigger } from 'src/provider/API/v2/call-service/Survey'
import { NewSurveyTriggerModal } from './NewSurveyTriggerModal'

const PAGE_PADDING_X = '3.2rem'

const SurveyManagementPage = () => {
    const navigate = useNavigate()
    const { getInstitutions } = useContext(ConfigurationApiContext)
    const { getSurveyTriggers } = useContext(SurveyApiContext)

    const [institutions, setInstitutions] = useState<InstitutionListItem[]>([])
    const [selectedInstitutionCode, setSelectedInstitutionCode] = useState('')
    const [triggers, setTriggers] = useState<SurveyTrigger[]>([])
    const [loading, setLoading] = useState(false)
    const [showAddTrigger, setShowAddTrigger] = useState(false)

    useEffect(() => {
        (async () => {
            const { items } = await getInstitutions({ FetchAll: true })
            setInstitutions(items ?? [])
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const fetchTriggers = async () => {
        if (!selectedInstitutionCode) return
        setLoading(true)
        const response = await getSurveyTriggers(selectedInstitutionCode)
        setTriggers(response || [])
        setLoading(false)
    }

    useEffect(() => {
        fetchTriggers()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedInstitutionCode])

    const viewTrigger = (trigger: SurveyTrigger) => {
        navigate(`/dashboardv2/survey-details?institutionCode=${selectedInstitutionCode}&triggerId=${trigger.id}`)
    }

    const columns: TableColumn<SurveyTrigger>[] = [
        {
            key: 'triggerName',
            title: 'Trigger Name',
            dataIndex: 'triggerName',
        },
        {
            key: 'triggerDescription',
            title: 'Description',
            dataIndex: 'triggerDescription',
        },
        {
            key: 'surveyInterval',
            title: 'Interval (days)',
            render: (row) => <Text fontSize="1.4" color={tokens.textMuted}>{row.surveyInterval}</Text>,
        },
        {
            key: 'createdAt',
            title: 'Created At',
            render: (row) => (
                <Text fontSize="1.4" color={tokens.textMuted}>
                    {row.createdAt ? new Date(row.createdAt).toLocaleString() : '-'}
                </Text>
            ),
        },
        {
            key: 'actions',
            title: '',
            align: 'right',
            render: (row) => (
                <Button variant="ghost" onClick={() => viewTrigger(row)}>View</Button>
            ),
        },
    ]

    return (
        <div style={{ padding: `2.4rem ${PAGE_PADDING_X}` }}>
            <Flex justify="space-between" items="center" wrap="wrap" gap="1.2">
                <Box>
                    <Text fontSize="2.4" fontWeight="700" color={tokens.textDark}>Survey Management</Text>
                    <Text topMargin="0.4" fontSize="1.4" color={tokens.textMuted}>
                        Create and manage survey triggers for an institution.
                    </Text>
                </Box>
                <Button disabled={!selectedInstitutionCode} onClick={() => setShowAddTrigger(true)}>
                    <FiPlus size={16} style={{ marginRight: '0.6rem' }} />
                    Add Trigger
                </Button>
            </Flex>

            <Box topMargin="2.4" style={{ maxWidth: '32rem' }}>
                <SelectField
                    label="Institution"
                    placeholder="Select an institution"
                    value={selectedInstitutionCode}
                    onChange={setSelectedInstitutionCode}
                    options={institutions.map((item) => ({ label: item.name, value: String(item.institutionCode) }))}
                />
            </Box>

            <Box topMargin="2.4" bgColor={tokens.white} borderColor={tokens.border} radius="1.2" shadow={tokens.shadowCard} style={{ overflow: 'hidden' }}>
                {!selectedInstitutionCode ? (
                    <Flex justify="center" style={{ padding: '4.8rem 0' }}>
                        <Text fontSize="1.4" color={tokens.textMuted}>Select an institution to view its survey triggers.</Text>
                    </Flex>
                ) : (
                    <InstitutionTable columns={columns} data={triggers} loading={loading} emptyText="No survey triggers found." />
                )}
            </Box>

            <NewSurveyTriggerModal
                open={showAddTrigger}
                onClose={() => setShowAddTrigger(false)}
                ibInstitutionCode={selectedInstitutionCode}
                onSaved={fetchTriggers}
            />
        </div>
    )
}

export default utility.routeData('survey-management', 'SurveyManagement', <SurveyManagementPage />)
